/**
 * Client-Helfer für den Design-Drawer (Issue #3 Paket B).
 *
 *   PUT  /api/admin/design            Farben + Schriften (functions/api/admin/design.js)
 *   PUT  /api/admin/content           Hintergrund-Blöcke (Bild + Stärke), 1:1 inkl. leerer Werte
 *   POST /api/admin/content/image     Bild-Upload nach R2 (via uploadContentImage)
 *
 * Auth läuft über das Session-Cookie des tenant_admin (kein API-Key); CSRF-Header
 * setzt apiFetch für /api/admin automatisch.
 */

import { apiFetch } from './api.js';
import { uploadContentImage } from './content.js';

export const ALLOWED_UPLOAD_TYPES = Object.freeze(['image/jpeg', 'image/png', 'image/webp']);
// Obergrenze für die Quelldatei im Browser (Dekodieren großer Bilder kostet Speicher).
// Der Server erlaubt 5 MB — größere Fotos verkleinern wir vorher automatisch.
export const MAX_SOURCE_BYTES = 25 * 1024 * 1024;
// Upload-Limit des Servers (functions/api/admin/content/image.js).
export const SERVER_MAX_BYTES = 5 * 1024 * 1024;

/**
 * Speichert Farben/Schriften. Schlüssel fehlt = unverändert, null = Standard.
 * @param {string} tenantId
 * @param {{primary_color?: string|null, secondary_color?: string|null, font_family?: string|null, font_heading?: string|null}} patch
 * @returns {Promise<{primaryColor: string, secondaryColor: string, fontFamily: string|null, fontHeading: string|null}>}
 */
export async function saveDesign(tenantId, patch) {
  if (!tenantId) {
    throw new Error('Kein Mandant ausgewählt. Speichern nicht möglich.');
  }
  const res = await apiFetch('/api/admin/design', {
    method: 'PUT',
    body: { tenant_id: tenantId, ...patch },
  });
  if (!res?.design) {
    throw new Error('Unerwartete Antwort des Servers.');
  }
  return res.design;
}

/**
 * Schreibt Content-Blöcke 1:1 (auch leere Werte). Ein leerer Text-Block ist der
 * Reset auf den Standard (blocksToMap lässt ihn weg). saveContent() würde leere
 * Werte herausfiltern und den Reset verhindern.
 * @param {string} tenantId
 * @param {string} page
 * @param {Array<{block_key: string, type: 'text'|'image', value?: string, image_url?: string, sort?: number}>} blocks
 */
export async function saveContentBlocks(tenantId, page, blocks) {
  if (!tenantId) {
    throw new Error('Kein Mandant ausgewählt. Speichern nicht möglich.');
  }
  const clean = blocks.map((block, index) => {
    const out = {
      block_key: block.block_key,
      type: block.type === 'image' ? 'image' : 'text',
      sort: Number.isFinite(block.sort) ? block.sort : index,
    };
    if (out.type === 'image') out.image_url = block.image_url;
    else out.value = typeof block.value === 'string' ? block.value : '';
    return out;
  });
  return apiFetch('/api/admin/content', {
    method: 'PUT',
    body: { tenant_id: tenantId, page, blocks: clean },
  });
}

/**
 * Entprellt Änderungen eines Feldes gegen den zuletzt ANGEFRAGTEN Wert (nicht nur den
 * bestätigten): Wer während eines laufenden Speicherns zum Ausgangswert zurückklickt
 * (Rot -> Blau -> Rot), muss auch den Rückweg speichern, sonst gewinnt der Zwischenstand.
 * Schlägt das zuletzt laufende Speichern fehl (send liefert false oder wirft), gilt wieder der
 * bestätigte Wert. Solange noch ein späterer Wert unterwegs ist, bleibt dieser maßgeblich.
 *
 * @template T
 * @param {T} initial bestätigter Anfangswert
 * @returns {{
 *   sync: (confirmed: T) => void,
 *   commit: (value: T, send: (value: T) => Promise<boolean|void>|boolean|void) => boolean,
 * }}
 */
export function createCommitGate(initial) {
  let confirmed = initial;
  let requested = initial;
  let inFlight = 0;

  return {
    /** Neuer bestätigter Stand vom Server. Während laufender Anfragen bleibt der angefragte Wert maßgeblich. */
    sync(value) {
      confirmed = value;
      if (inFlight === 0) requested = value;
    },
    /** @returns {boolean} true, wenn tatsächlich gesendet wurde */
    commit(value, send) {
      if (value === null || value === undefined || value === requested) return false;
      requested = value;
      inFlight += 1;
      // async-IIFE: send() läuft synchron los (Reihenfolge der Bedienung bleibt erhalten).
      (async () => {
        let failed = false;
        try {
          const ok = await send(value);
          if (ok === false) failed = true;
        } catch {
          failed = true;
        } finally {
          inFlight -= 1;
          // Erst zurücksetzen, wenn nichts mehr unterwegs ist: Läuft noch ein späterer Wert, bleibt der
          // zuletzt angefragte maßgeblich, sonst ginge ein Rückweg verloren, während dieser Job noch speichert.
          if (failed && inFlight === 0) requested = confirmed;
        }
      })();
      return true;
    },
  };
}

const KNOWN_ERRORS = {
  content_delivery_not_configured: 'Der Bild-Upload ist für diesen Shop noch nicht eingerichtet.',
  content_storage_not_configured: 'Der Bild-Upload ist für diesen Shop noch nicht eingerichtet.',
};

/**
 * Verständliche deutsche Meldung für einen Fehler beim Speichern oder Hochladen. Technische Fehler
 * (Netz, Sitzung, Limits, Server) werden übersetzt; wörtlich bleiben nur Prüfmeldungen des Servers
 * (400, deutsch formuliert) und Meldungen aus dem Browser (kein Status, z. B. „Das Foto konnte nicht
 * verarbeitet werden“). Rohe API-Codes wie „csrf_failed“ erreichen niemanden.
 */
export function describeSaveError(error) {
  const message = typeof error?.message === 'string' ? error.message : '';
  if (KNOWN_ERRORS[message]) return KNOWN_ERRORS[message];
  const status = error?.status;
  if (status === 0) return 'Keine Verbindung zum Server. Bitte Verbindung prüfen und erneut versuchen.';
  if (status === 401) return 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.';
  if (status === 403) return 'Für diese Änderung fehlt die Berechtigung. Bitte erneut anmelden.';
  if (status === 413) return 'Das Bild ist zu groß. Bitte ein kleineres Bild wählen.';
  if (status === 415) return 'Dieses Format wird nicht unterstützt. Bitte JPG, PNG oder WebP verwenden.';
  if (status === 429) return 'Zu viele Anfragen. Bitte einen Moment warten und erneut versuchen.';
  if (status >= 500) return 'Der Server konnte nicht speichern. Bitte später erneut versuchen.';
  if ((status === undefined || status === null || status === 400) && message) return message;
  return 'Speichern fehlgeschlagen. Bitte erneut versuchen.';
}

/** Blöcke, die ein Hintergrundbild eines Slots setzen. */
export function backgroundSetBlocks(slot, imageUrl) {
  return [{ block_key: slot.imageKey, type: 'image', image_url: imageUrl, sort: 0 }];
}

/** Blöcke, die Hintergrundbild + Stärke eines Slots zurücksetzen (leerer Text-Block = Standard). */
export function backgroundClearBlocks(slot, { includeStrength = false } = {}) {
  const blocks = [{ block_key: slot.imageKey, type: 'text', value: '', sort: 0 }];
  if (includeStrength) blocks.push({ block_key: slot.strengthKey, type: 'text', value: '', sort: 1 });
  return blocks;
}

export function backgroundStrengthBlocks(slot, strength) {
  return [{ block_key: slot.strengthKey, type: 'text', value: String(strength), sort: 1 }];
}

/**
 * Früher Dateicheck für das Bild-Upload-Feld. Gibt eine deutsche Fehlermeldung
 * zurück oder null, wenn die Datei in Frage kommt (der Server prüft zusätzlich
 * MIME und Magic Bytes).
 */
export function validateImageFile(file) {
  if (!file || typeof file !== 'object') return 'Bitte wählen Sie eine Bilddatei aus.';
  if (!ALLOWED_UPLOAD_TYPES.includes(String(file.type || '').toLowerCase())) {
    return 'Dieses Format wird nicht unterstützt. Bitte JPG, PNG oder WebP verwenden.';
  }
  if (!(file.size > 0)) return 'Die Datei ist leer.';
  if (file.size > MAX_SOURCE_BYTES) return 'Das Bild ist zu groß (maximal 25 MB).';
  return null;
}

/**
 * Skaliert (width, height) proportional so, dass die längste Kante maxEdge
 * nicht überschreitet. Vergrößert nie.
 */
export function fitWithin(width, height, maxEdge) {
  const longest = Math.max(width, height);
  if (!(longest > maxEdge)) return { width, height, scaled: false };
  const ratio = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
    scaled: true,
  };
}

/** Bewusst formulierte Meldung der Bildaufbereitung (wird unverändert angezeigt). */
class ImagePrepareError extends Error {}

/**
 * Verkleinert große Fotos vor dem Upload (Server-Limit 5 MB, und ein 6-MB-Hero wäre für
 * Besucher viel zu schwer). PNG/WebP bleiben unverändert, wenn sie klein genug sind.
 *
 * JPEGs werden IMMER neu kodiert: Handyfotos tragen EXIF-Daten (GPS-Standort, Gerät, Zeitpunkt),
 * und das Bild wird öffentlich ausgeliefert; die Canvas-Kodierung schreibt keine Metadaten mit.
 * Gelingt das bei einem JPEG nicht (Decoder, Canvas, toBlob, Größe), bricht der Upload mit einer
 * deutschen Meldung ab: Das Original geht nie hoch. Bei PNG/WebP fällt jeder Fehler auf die
 * Originaldatei zurück, der Server entscheidet dann. Jedes neu kodierte Bild ist JPEG (weißer
 * Grund, Transparenz entfällt) und wird bei mehr als 5 MB gröber nachkodiert, sonst abgelehnt.
 * Bekannte Grenzen: PNG/WebP, die unverändert durchgehen, können eigene Metadaten-Chunks tragen;
 * Browser, die imageOrientation nicht kennen, richten Hochformatfotos nach ihrem Standard aus.
 *
 * @param {File} file
 * @param {{maxEdge?: number, passThroughBytes?: number, quality?: number}} [options]
 * @returns {Promise<File>}
 */
export async function prepareImageForUpload(file, { maxEdge = 2400, passThroughBytes = 1.5 * 1024 * 1024, quality = 0.86 } = {}) {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;

  const carriesExif = String(file.type || '').toLowerCase() === 'image/jpeg';
  // Ein JPEG darf nie unverarbeitet (samt GPS) durchgehen, nur weil die Aufbereitung scheitert.
  const giveUp = () => {
    if (carriesExif) throw new ImagePrepareError('Das Foto konnte nicht verarbeitet werden. Bitte ein anderes Bild wählen.');
    return file;
  };

  let bitmap = null;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // Ältere Browser kennen die Option nicht: einmal ohne Option versuchen.
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      bitmap = null;
    }
  }
  if (!bitmap) return giveUp();

  try {
    const target = fitWithin(bitmap.width, bitmap.height, maxEdge);
    if (!target.scaled && file.size <= passThroughBytes && !carriesExif) return file;

    const canvas = document.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return giveUp();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, target.width, target.height);
    ctx.drawImage(bitmap, 0, 0, target.width, target.height);

    const encode = (q) => new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', q));
    let blob = await encode(quality);
    if (!blob) return giveUp();

    // PNG/WebP in Originalgröße: nie größer machen als nötig.
    if (!target.scaled && !carriesExif && blob.size >= file.size) return file;

    // Über dem Server-Limit oder (unskaliertes JPEG) größer als die Quelle: gröber nachkodieren.
    const tooBig = (candidate) =>
      candidate.size > SERVER_MAX_BYTES || (carriesExif && !target.scaled && candidate.size > file.size);
    for (const coarserQuality of [0.7, 0.55].filter((q) => q < quality)) {
      if (!tooBig(blob)) break;
      const coarser = await encode(coarserQuality);
      if (coarser && coarser.size < blob.size) blob = coarser;
    }
    if (blob.size > SERVER_MAX_BYTES) {
      throw new ImagePrepareError('Das Foto ist nach der Aufbereitung noch zu groß. Bitte ein kleineres Bild wählen.');
    }

    const baseName = String(file.name || 'bild').replace(/\.[^.]+$/, '') || 'bild';
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
  } catch (error) {
    // Unerwartete Fehler (Canvas, Speicher, File-Konstruktor) wie nicht verarbeitbar behandeln;
    // bewusst formulierte Meldungen bleiben erhalten.
    if (error instanceof ImagePrepareError) throw error;
    return giveUp();
  } finally {
    if (typeof bitmap.close === 'function') bitmap.close();
  }
}

/**
 * Datei prüfen, verkleinern, hochladen. Gibt die Bild-URL zurück (Same-Origin
 * /media/..., siehe functions/media/[[path]].js).
 */
export async function uploadBackgroundImage(file) {
  const problem = validateImageFile(file);
  if (problem) throw new Error(problem);
  const prepared = await prepareImageForUpload(file);
  const { image_url: imageUrl } = await uploadContentImage(prepared);
  return imageUrl;
}
